import { useRef, forwardRef, useMemo, useState } from "react";

//ANT DESIGN IMPORTS
import { Row, Col, Table, Button } from "antd";
import { PrinterOutlined, FileExcelOutlined } from "@ant-design/icons";

import { CSVLink } from "react-csv";
import ReactToPrint from "react-to-print";

//PROJECT IMPORTS
import appLabel from "../config/appLabel";
import { BOM } from "../services/definedMethod";


function DownloadTableButtons(props) {
  let componentRef = useRef();
  const csvLinkRef = useRef();

  const { downloadData, columns, fileName, onExportClick } = props;

  const [isExporting, setIsExporting] = useState(false);
  const [exportData, setExportData] = useState(null);

  const customFileName = BOM.ReplaceSpace(
    `${fileName ? fileName : ""} ${BOM.GenerateTimestamp()}`,
    "_"
  );

  // Helper function to extract text from React elements
  const extractTextFromReactElement = (element) => {
    if (typeof element === "string") return element;
    if (typeof element === "number") return element.toString();
    if (!element) return "";

    if (typeof element === "object" && element.props) {
      if (element.props.children) {
        if (typeof element.props.children === "string") {
          return element.props.children;
        }
        if (Array.isArray(element.props.children)) {
          return element.props.children
            .map((child) =>
              typeof child === "string"
                ? child
                : extractTextFromReactElement(child)
            )
            .join("");
        }
        return extractTextFromReactElement(element.props.children);
      }
    }

    return element.toString();
  };

  // Handle export button click
  const handleExportClick = async () => {
    if (!onExportClick) {
      // No fetch function provided, just export current data
      csvLinkRef.current?.link?.click();
      return;
    }

    setIsExporting(true);

    try {
      // Call the fetch function from parent
      const fullData = await onExportClick();

      if (fullData && fullData.length > 0) {
        setExportData(fullData);

        // Trigger CSV download after data is set
        setTimeout(() => {
          csvLinkRef.current?.link?.click();
          setExportData(null); // Reset
          setIsExporting(false);
        }, 100);
      } else {
        BOM.AlertMsg("warning", "No data to export");
        setIsExporting(false);
      }
    } catch (error) {
      BOM.AlertMsg("danger", "Failed to fetch export data");
      console.error("Export error:", error);
      setIsExporting(false);
    }
  };

  // Use export data if available, otherwise use current page data
  const dataToExport = exportData || downloadData;

  // Process data for CSV export to handle objects and React elements
  const processedDataForCSV = useMemo(() => {
    return dataToExport.map((item) => {
      const processedItem = {};

      columns.forEach((column) => {
        const key = column.dataIndex;
        let value = item[key];

        // Skip otherKeys field as it's metadata
        if (key === "otherKeys") {
          return;
        }

        // Handle React elements (like your status field)
        if (value && typeof value === "object" && value.props) {
          processedItem[key] = extractTextFromReactElement(value);
        }

        // Handle arrays and objects
        else if (typeof value === "object" && value !== null) {
          if (Array.isArray(value)) {
            // Join array items with comma for CSV readability
            processedItem[key] = value.join(", ");
          } else if (
            key === "status" &&
            item.otherKeys &&
            item.otherKeys.statusValue
          ) {
            processedItem[key] = item.otherKeys.statusValue;
          } else {
            processedItem[key] = JSON.stringify(value);
          }
        }

        // Handle primitive values
        else {
          if (typeof value === "string" && /^0\d+/.test(value)) {
            // Force Excel to treat as text
            // processedItem[key] = `'${value}`;
            processedItem[key] = `\t${value}`;
          } else {
            processedItem[key] = value || "-";
          }
        }
      });

      return processedItem;
    });
  }, [dataToExport, columns]);

  // Filter columns for CSV export (exclude otherKeys) and process titles
  const csvColumns = columns
    .filter((col) => col.dataIndex !== "otherKeys")
    .map((col) => {
      let titleText = col.title;

      if (typeof col.title === "function") {
        // Fallback: use dataIndex and make it sentence case
        const text = col.dataIndex
          .replace(/([A-Z])/g, " $1") // split camelCase
          .replace(/^./, (str) => str.toUpperCase()); // capitalize first letter
        titleText = text.trim();
      }

      return {
        ...col,
        title: titleText,
      };
    });

  // Process data for print (keeping React elements for proper rendering)
  const processedDataForPrint = useMemo(() => {
    return downloadData.map((item) => {
      const processedItem = { ...item };

      // Remove otherKeys from print data
      delete processedItem.otherKeys;

      return processedItem;
    });
  }, [downloadData]);

  // Filter columns for print (exclude otherKeys) and process titles for print headers
  const printColumns = columns
    .filter((col) => col.dataIndex !== "otherKeys")
    .map((col) => ({
      ...col,
      title:
        typeof col.title === "function"
          ? extractTextFromReactElement(col.title())
          : col.title,
    }));

  const buttonBaseStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "32px",
    padding: "0 16px",
    borderRadius: "10px",
    backgroundColor: "white",
    fontWeight: 500,
    border: "1px solid #d9d9d9",
  };

  const iconStyle = {
    marginRight: "6px",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
  };

  return (
    <Row style={{ marginLeft: "auto", marginBottom: "auto" }}>
      <Col style={{ padding: "0px 4px" }}>
        <Button
          style={buttonBaseStyle}
          onClick={handleExportClick}
          loading={isExporting}
          disabled={isExporting}
        >
          {!isExporting && <FileExcelOutlined style={iconStyle} />}
          {isExporting ? "Exporting..." : appLabel.exportCSV}
        </Button>

        {/* Hidden CSVLink triggered programmatically */}
        <CSVLink
          ref={csvLinkRef}
          data={processedDataForCSV}
          filename={customFileName}
          headers={csvColumns.map((col, index) => ({
            label: col.title || col.dataIndex || "Unknown Column",
            key: col.dataIndex || `column_${index}`,
          }))}
          style={{ display: "none" }}
        />
      </Col>

      <Col style={{ padding: "0px 4px" }}>
        <ReactToPrint
          trigger={() => (
            <Button style={buttonBaseStyle}>
              <PrinterOutlined style={iconStyle} />
              {appLabel.print}
            </Button>
          )}
          content={() => componentRef}
        />
      </Col>

      <span style={{ display: "none" }}>
        <ComponentToPrint
          ref={(el) => (componentRef = el)}
          columns={printColumns}
          listData={processedDataForPrint}
        />
      </span>
    </Row>
  );
}

const ComponentToPrint = forwardRef((props, ref) => {
  const { columns, listData } = props;

  return (
    <div ref={ref}>
      <Table
        columns={columns}
        dataSource={listData}
        pagination={false}
        bordered
      />
    </div>
  );
});

export default DownloadTableButtons;
